import { DateTime } from "luxon";
import { type ReactNode, useState } from "react";
import styled from "styled-components";
import { Dropdown, type Item, Status } from "../../../../components/ui-kit";
import {
  CardEditButton,
  DetailCard,
  DetailCardRow,
  DetailCardStatusRow,
} from "./index";
import {
  DETAIL_ACTION_DEFAULT_CONFIRM_MESSAGE,
  DETAIL_ACTION_DROPDOWN_PLACEHOLDER,
  type DetailCardConfig,
  type DetailCardsConfig,
  type DetailFieldAction,
  type DetailFieldConfig,
  type DetailVisibility,
} from "./detail-cards-config.types";

const StatusActionsDropdownWrap = styled.div`
  min-width: 170px;
`;

function isVisible<T>(
  hidden: DetailVisibility<T> | undefined,
  data: T
): boolean {
  if (hidden === undefined) return true;
  const isHidden = typeof hidden === "function" ? hidden(data) : hidden;
  return !isHidden;
}

function getValueByAccessor(data: unknown, accessorKey?: string): unknown {
  if (!accessorKey) return undefined;
  return accessorKey.split(".").reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== "object") return undefined;
    return (acc as Record<string, unknown>)[key];
  }, data);
}

function resolveFieldValue<T>(field: DetailFieldConfig<T>, data: T): unknown {
  if (field.getValue) return field.getValue(data);
  return getValueByAccessor(data, field.accessorKey);
}

function renderDateValue(value: unknown, format?: string): string | undefined {
  if (value == null || String(value).trim() === "") return undefined;
  return DateTime.fromISO(String(value)).toFormat(
    format ?? "LLLL dd, yyyy HH:mm:ss"
  );
}

function isActionDisabled<T>(action: DetailFieldAction<T>, data: T): boolean {
  if (action.disabled === undefined) return false;
  return typeof action.disabled === "function"
    ? action.disabled(data)
    : action.disabled;
}

function buildActionItems<T>(
  field: Extract<DetailFieldConfig<T>, { displayType: "status" }>,
  data: T,
  onActionComplete: () => void
): Item[] {
  const actionsHidden =
    field.actionsHidden !== undefined && !isVisible(field.actionsHidden, data);
  if (actionsHidden || !field.actions?.length) return [];

  return field.actions
    .filter((action) => isVisible(action.hidden, data))
    .filter((action) => !isActionDisabled(action, data))
    .map((action, index) => ({
      label: action.label,
      value: `${action.label}-${index}`,
      onClick: async () => {
        const confirmMessage =
          action.confirmMessage ?? DETAIL_ACTION_DEFAULT_CONFIRM_MESSAGE;
        if (!window.confirm(confirmMessage)) return;
        await action.onClick(data);
        onActionComplete();
      },
    }));
}

function renderFieldValue<T>(field: DetailFieldConfig<T>, data: T): ReactNode {
  const rawValue = resolveFieldValue(field, data);

  switch (field.displayType) {
    case "text":
      return rawValue as ReactNode;
    case "date":
      return renderDateValue(rawValue, field.format);
    case "boolean":
      if (rawValue == null) return undefined;
      return rawValue
        ? field.mapValue?.true ?? "Yes"
        : field.mapValue?.false ?? "No";
    case "status": {
      if (rawValue == null) return undefined;
      const mapped = field.mapValue
        ? field.mapValue[String(rawValue)]
        : undefined;
      return <Status status={(mapped ?? rawValue) as string} />;
    }
    case "link": {
      const href = field.getHref(data);
      if (!href) return undefined;
      const label =
        field.getLabel?.(data) ??
        (rawValue == null ? undefined : String(rawValue).trim() || undefined);
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {label ?? href}
        </a>
      );
    }
    case "email": {
      const email =
        rawValue == null ? undefined : String(rawValue).trim() || undefined;
      if (!email) return undefined;
      return <a href={`mailto:${email}`}>{email}</a>;
    }
    case "custom":
      return field.render(data);
    default:
      return undefined;
  }
}

function DetailStatusActionsDropdown<T>({
  field,
  data,
}: {
  field: Extract<DetailFieldConfig<T>, { displayType: "status" }>;
  data: T;
}) {
  const [dropdownKey, setDropdownKey] = useState(0);
  const actionItems = buildActionItems(field, data, () =>
    setDropdownKey((current) => current + 1)
  );
  if (actionItems.length === 0) return null;

  const placeholder =
    field.actionsPlaceholder ?? DETAIL_ACTION_DROPDOWN_PLACEHOLDER;
  const defaultItem: Item = { label: placeholder, value: "__placeholder__" };

  return (
    <StatusActionsDropdownWrap>
      <Dropdown
        key={dropdownKey}
        fullWidth
        items={actionItems}
        defaultItem={defaultItem}
        onChange={(item) => {
          void item.onClick?.();
        }}
      />
    </StatusActionsDropdownWrap>
  );
}

export function ConfigurableDetailCard<T>({
  config,
  data,
}: {
  config: DetailCardConfig<T>;
  data: T;
}) {
  if (!isVisible(config.hidden, data)) return null;

  const visibleFields = config.fields.filter((field) =>
    isVisible(field.hidden, data)
  );

  return (
    <DetailCard
      title={config.title}
      headerAction={
        config.editable && config.onEdit ? (
          <CardEditButton onClick={config.onEdit}>Edit</CardEditButton>
        ) : undefined
      }
    >
      {visibleFields.map((field) =>
        field.displayType === "status" ? (
          <DetailCardStatusRow
            key={field.label}
            label={field.label}
            status={renderFieldValue(field, data)}
            actions={
              buildActionItems(field, data, () => undefined).length > 0 ? (
                <DetailStatusActionsDropdown field={field} data={data} />
              ) : null
            }
          />
        ) : (
          <DetailCardRow
            key={field.label}
            label={field.label}
            value={renderFieldValue(field, data)}
          />
        )
      )}
    </DetailCard>
  );
}

export function ConfigurableDetailCards<T>({
  config,
  data,
}: {
  config: DetailCardsConfig<T>;
  data: T;
}) {
  return (
    <>
      {config.cards.map((card) => (
        <ConfigurableDetailCard key={card.title} config={card} data={data} />
      ))}
    </>
  );
}
